/**
 * Firestore implementation of FloatLedger + FloatEntry repositories.
 *
 * Paths:
 *   cashFloatLedgers/{id}
 *   cashFloatEntries/{id}  — flat collection; queried by ledgerId
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { EntityId, FloatEntry, FloatLedger } from '../../../types';
import type { FloatEntryRepository, FloatLedgerRepository } from '../types';

const LEDGER_COLLECTION = 'cashFloatLedgers';
const ENTRY_COLLECTION = 'cashFloatEntries';

export const firestoreFloatLedgerRepository: FloatLedgerRepository = {
  async findById(id: EntityId): Promise<FloatLedger | null> {
    const snap = await getDoc(doc(db, LEDGER_COLLECTION, id));
    return snap.exists() ? (snap.data() as FloatLedger) : null;
  },

  async save(entity: FloatLedger): Promise<void> {
    await setDoc(doc(db, LEDGER_COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, LEDGER_COLLECTION, id));
  },

  async list(): Promise<FloatLedger[]> {
    const snap = await getDocs(collection(db, LEDGER_COLLECTION));
    return snap.docs.map((d) => d.data() as FloatLedger);
  },

  async findByHolder(holderEmail: string): Promise<FloatLedger[]> {
    const q = query(collection(db, LEDGER_COLLECTION), where('holderEmail', '==', holderEmail));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as FloatLedger);
  },
};

export const firestoreFloatEntryRepository: FloatEntryRepository = {
  async findById(id: EntityId): Promise<FloatEntry | null> {
    const snap = await getDoc(doc(db, ENTRY_COLLECTION, id));
    return snap.exists() ? (snap.data() as FloatEntry) : null;
  },

  async save(entity: FloatEntry): Promise<void> {
    await setDoc(doc(db, ENTRY_COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, ENTRY_COLLECTION, id));
  },

  async findByLedger(ledgerId: EntityId): Promise<FloatEntry[]> {
    const q = query(collection(db, ENTRY_COLLECTION), where('ledgerId', '==', ledgerId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as FloatEntry);
  },
};
